/**
 * Pure helpers for reflecting the store-page review filters (source / rating / sort / images)
 * in the URL query string, so a filtered reviews view can be shared as a link.
 *
 * No React and no browser globals — safe to import during SSR.
 */

export const REVIEW_SORT_VALUES = [
  "newest",
  "oldest",
  "rating_high",
  "rating_low",
  "most_liked",
] as const;

export type ReviewSort = (typeof REVIEW_SORT_VALUES)[number];

export type ReviewFilterState = {
  source: string;
  rating?: number;
  sort: ReviewSort;
  // "with" → only reviews carrying at least one image. Absent → no image condition.
  images?: "with";
};

export const REVIEW_FILTER_DEFAULTS: ReviewFilterState = {
  source: "all",
  rating: undefined,
  sort: "newest",
  images: undefined,
};

export const REVIEW_FILTER_PARAM_KEYS = ["source", "rating", "sort", "images"] as const;

const SOURCE_PATTERN = /^[a-z0-9_]+$/i;
const SOURCE_MAX_LENGTH = 40;

const parseSort = (raw: string | null): ReviewSort => {
  if (!raw) return REVIEW_FILTER_DEFAULTS.sort;
  return (REVIEW_SORT_VALUES as readonly string[]).includes(raw)
    ? (raw as ReviewSort)
    : REVIEW_FILTER_DEFAULTS.sort;
};

const parseRating = (raw: string | null): number | undefined => {
  if (!raw) return undefined;
  const value = Number(raw);
  return Number.isInteger(value) && value >= 1 && value <= 5 ? value : undefined;
};

const parseImages = (raw: string | null): "with" | undefined =>
  raw === "with" ? "with" : undefined;

const parseSource = (raw: string | null): string => {
  if (!raw) return REVIEW_FILTER_DEFAULTS.source;
  const trimmed = raw.trim();
  if (!trimmed || trimmed.length > SOURCE_MAX_LENGTH || !SOURCE_PATTERN.test(trimmed)) {
    return REVIEW_FILTER_DEFAULTS.source;
  }
  return trimmed;
};

/**
 * Defensively parse the review filter params. Anything malformed
 * (unknown sort, out-of-range rating, weird source) degrades to the default.
 * `hasAny` is true only when a param was present AND parsed to a non-default
 * value — it drives scroll-on-load, so bogus params must not trigger a scroll.
 */
export const parseReviewFilterParams = (
  params: URLSearchParams
): { state: ReviewFilterState; hasAny: boolean } => {
  const source = parseSource(params.get("source"));
  const rating = parseRating(params.get("rating"));
  const sort = parseSort(params.get("sort"));
  const images = parseImages(params.get("images"));

  const hasAny =
    (params.has("source") && source !== REVIEW_FILTER_DEFAULTS.source) ||
    (params.has("rating") && rating !== undefined) ||
    (params.has("sort") && sort !== REVIEW_FILTER_DEFAULTS.sort) ||
    images !== undefined;

  return { state: { source, rating, sort, images }, hasAny };
};

/**
 * Serialize filter state back into a query string, preserving unrelated params
 * already on the URL and writing only non-default values.
 */
export const applyReviewFilterParams = (search: string, state: ReviewFilterState): string => {
  const params = new URLSearchParams(search);
  for (const key of REVIEW_FILTER_PARAM_KEYS) params.delete(key);

  if (state.source && state.source !== REVIEW_FILTER_DEFAULTS.source) {
    params.set("source", state.source);
  }
  if (state.rating !== undefined) params.set("rating", String(state.rating));
  if (state.sort && state.sort !== REVIEW_FILTER_DEFAULTS.sort) params.set("sort", state.sort);
  if (state.images) params.set("images", state.images);

  const query = params.toString();
  return query ? `?${query}` : "";
};

/** Absolute, `#reviews`-anchored link to the currently filtered reviews view. */
export const buildReviewShareUrl = ({
  origin,
  pathname,
  search,
  state,
}: {
  origin: string;
  pathname: string;
  search: string;
  state: ReviewFilterState;
}): string => `${origin}${pathname}${applyReviewFilterParams(search, state)}#reviews`;
